'use client'

import { useTranslations } from 'next-intl'
import { SheetPortal } from './SheetPortal'
import { AddressStep } from './sheet/AddressStep'
import type { DropoffAddress, HeldQuote } from './types'

/**
 * El paso de dirección FUERA del checkout (spec 2026-09-03 §4.3): lo abre la
 * barra "¿Cómo lo recibes?". Reusa `AddressStep` sin cambiarle nada más que el
 * texto del botón principal ("Listo": confirma y cierra).
 *
 * No se abre el OrderSheet entero para esto: arrastraría el carrito, el upsell
 * y el resto del checkout a un momento en el que el cliente aún no eligió comida.
 *
 * Contenedor = el de ItemModal (sheet abajo en móvil, centrado desde `sm`).
 */
export interface AddressSheetProps {
  open: boolean
  locationId: string
  accentColor: string
  dropoff: DropoffAddress | null
  heldQuote: HeldQuote | null
  /** El padre guarda quote + dirección y pone modo domicilio — tras pasar el cerrojo (acceptQuoteGate). */
  onQuote: (quote: HeldQuote, dropoff: DropoffAddress) => void
  onClearQuote: () => void
  /** "Mejor recojo yo": el padre vuelve a pickup, limpia y cierra. */
  onSwitchToPickup: () => void
  onClose: () => void
  /** Abre el explicador "¿Por qué cobramos envío?"; pasa tal cual a AddressStep. */
  onWhyFee?: () => void
}

export function AddressSheet({
  open,
  locationId,
  accentColor,
  dropoff,
  heldQuote,
  onQuote,
  onClearQuote,
  onSwitchToPickup,
  onClose,
  onWhyFee,
}: AddressSheetProps) {
  const t = useTranslations('public.onlineOrder.checkout')
  if (!open) return null

  return (
    <SheetPortal>
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-label={t('addressTitle')}
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-2xl bg-white text-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
          style={{ maxHeight: '90dvh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 flex justify-center pt-2.5 pb-1">
            <div className="w-10 h-1.5 rounded-full bg-gray-300" />
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <AddressStep
              locationId={locationId}
              accentColor={accentColor}
              dropoff={dropoff}
              heldQuote={heldQuote}
              onQuote={onQuote}
              onClearQuote={onClearQuote}
              onSwitchToPickup={onSwitchToPickup}
              onContinue={onClose}
              onBack={onClose}
              continueLabel={t('addressDone')}
              onWhyFee={onWhyFee}
            />
          </div>
        </div>
      </div>
    </SheetPortal>
  )
}
